import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, HandCoins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, ConfirmDialog, Field, LockedTag, Money } from "../components/common";
import { AnakCombobox, PeriodPicker, RupiahInput } from "../components/inputs";
import { api, errMsg } from "../lib/api";
import { currentPeriod, fmtDate, rp, todayISO } from "../lib/format";

const KasbonForm = ({ anak, initial, onSubmit, quick, prefix }) => {
  const [anakId, setAnakId] = useState(initial?.anak_id || "");
  const [tanggal, setTanggal] = useState(initial?.tanggal || todayISO());
  const [jumlah, setJumlah] = useState(initial ? String(initial.jumlah) : "");
  const [ket, setKet] = useState(initial?.keterangan || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!anakId) return toast.error("Pilih anak terlebih dahulu");
    if (!(Number(jumlah) > 0)) return toast.error("Jumlah kasbon harus lebih dari Rp0");
    if (!tanggal) return toast.error("Tanggal wajib diisi");
    setSaving(true);
    const ok = await onSubmit({ anak_id: anakId, tanggal, jumlah: Number(jumlah), keterangan: ket.trim() || null });
    setSaving(false);
    if (ok && quick) { setAnakId(""); setJumlah(""); setKet(""); }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1.1fr_1.4fr]" data-testid={`${prefix}-form`}>
      <Field label="Anak"><AnakCombobox anak={anak.filter((a) => a.aktif || a.id === anakId)} value={anakId} onChange={setAnakId} testid={`${prefix}-anak`} /></Field>
      <Field label="Tanggal"><Input type="date" data-testid={`${prefix}-tanggal`} className="h-10 bg-white" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></Field>
      <Field label="Jumlah"><RupiahInput value={jumlah} onChange={setJumlah} testid={`${prefix}-jumlah`} placeholder="0" /></Field>
      <Field label="Keterangan"><Input data-testid={`${prefix}-keterangan`} className="h-10 bg-white" value={ket} onChange={(e) => setKet(e.target.value)} placeholder="opsional" /></Field>
      <div className="flex justify-end md:col-span-2 lg:col-span-4">
        <Button data-testid={`${prefix}-submit`} type="submit" disabled={saving} className="h-10 w-full rounded-full px-5 text-base active:scale-[0.98] sm:w-auto">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial ? "Simpan perubahan" : "Simpan kasbon"}
        </Button>
      </div>
    </form>
  );
};

export default function KasbonPage() {
  const [anak, setAnak] = useState([]);
  const [period, setPeriod] = useState(currentPeriod());
  const [allPeriods, setAllPeriods] = useState(false);
  const [fAnak, setFAnak] = useState("");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  useEffect(() => { api.get("/anak").then((r) => setAnak(r.data)); }, []);

  const load = useCallback(() => {
    api.get("/kasbon", { params: { periode: allPeriods ? undefined : period, anak_id: fAnak || undefined } })
      .then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, fAnak, allPeriods]);
  useEffect(() => { load(); }, [load]);

  const create = async (body) => {
    try { await api.post("/kasbon", body); toast.success("Kasbon berhasil dicatat"); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const update = async (body) => {
    try { await api.put(`/kasbon/${editing.id}`, body); toast.success("Kasbon diperbarui"); setEditing(null); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const doDelete = async () => {
    try { await api.delete(`/kasbon/${del.id}`); toast.success("Kasbon dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  const total = (items || []).reduce((s, k) => s + k.jumlah, 0);
  return (
    <div>
      <PageHeader title="Kasbon" testid="kasbon-title" subtitle="Kasbon dipotong otomatis saat gajian. Sisa yang belum terpotong dibawa ke periode berikutnya." />

      <section className="fade-up mb-5 rounded-2xl border border-amber-200 bg-white p-4 sm:p-4">
        <p className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-gray-900"><HandCoins className="h-4 w-4 text-amber-600" />Catat kasbon</p>
        {anak.length === 0 ? <p className="text-sm text-gray-500">Tambahkan anak terlebih dahulu di menu Anak.</p> :
          <KasbonForm anak={anak} onSubmit={create} quick prefix="quick-kasbon" />}
      </section>

      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
        <div className={allPeriods ? "pointer-events-none opacity-40" : ""}><PeriodPicker value={period} onChange={setPeriod} testid="kasbon-period" /></div>
        <AnakCombobox anak={anak} value={fAnak} onChange={setFAnak} allowAll testid="kasbon-filter-anak" />
        <Button data-testid="kasbon-all-periods-toggle" variant={allPeriods ? "default" : "outline"} className="h-10" onClick={() => setAllPeriods((v) => !v)}>
          Semua periode
        </Button>
      </div>
      <p data-testid="kasbon-summary" className="mb-3 text-sm text-gray-500">
        {items ? <>{items.length} entri · total <span className="font-mono font-semibold text-amber-700">{rp(total)}</span></> : "Memuat…"}
      </p>

      {items && items.length === 0 ? <EmptyState icon={HandCoins} title="Belum ada kasbon" desc="Tidak ada kasbon pada filter ini." /> : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {(items || []).map((k, i) => (
            <div key={k.id} data-testid={`kasbon-row-${k.id}`} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }} className="fade-up flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-gray-900">{k.anak_nama || "-"}</p>
                <p className="break-words text-[13px] text-gray-500">{fmtDate(k.tanggal)}{k.keterangan ? ` · ${k.keterangan}` : ""}</p>
              </div>
              <Money value={k.jumlah} className="text-sm font-semibold text-amber-700" />
              {k.locked ? <LockedTag testid={`kasbon-locked-${k.id}`} /> : (
                <div className="flex shrink-0">
                  <Button data-testid={`kasbon-edit-${k.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => setEditing(k)} aria-label="Ubah"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`kasbon-delete-${k.id}`} variant="ghost" size="icon" className="h-9 w-9 text-red-600 hover:bg-red-50" onClick={() => setDel(k)} aria-label="Hapus"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="kasbon-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ubah kasbon</DialogTitle></DialogHeader>
          {editing && <KasbonForm anak={anak} initial={editing} onSubmit={update} prefix="edit-kasbon" />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title="Hapus kasbon?"
        description={del ? `Kasbon ${rp(del.jumlah)} milik ${del.anak_nama} tanggal ${fmtDate(del.tanggal)} akan dihapus.` : ""} confirmLabel="Hapus" onConfirm={doDelete} testid="kasbon-delete-confirm" />
    </div>
  );
}
